import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import {
  ArrowLeft,
  FileText,
  Download,
  RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DocumentosPWA = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [documentos, setDocumentos] = useState([]);

  useEffect(() => {
    carregarDocumentos();
  }, []);

  const carregarDocumentos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/timetracking/listar-espelhos-assinados`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('Documentos recebidos:', response.data);
      setDocumentos(response.data.documentos || []);
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || 'Erro ao carregar documentos',
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const baixarPDF = async (documento) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/timetracking/download-pdf-espelho/${documento.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Criar URL do blob e fazer download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `folha_ponto_${documento.funcionario_nome}_${documento.periodo_inicio}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Sucesso!",
        description: "PDF baixado com sucesso",
        variant: "default"
      });
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
      toast({
        title: "Erro",
        description: "Erro ao baixar PDF",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 shadow-lg sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => window.history.back()}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div className="flex items-center space-x-2">
              <FileText className="h-6 w-6" />
              <h1 className="text-xl font-bold">Documentos Assinados</h1>
            </div>
          </div>
          <button
            onClick={carregarDocumentos}
            disabled={loading}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Loading State */}
        {loading && documentos.length === 0 ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Carregando documentos...</p>
          </div>
        ) : documentos.length === 0 ? (
          /* Empty State */
          <Card className="border-2 border-dashed">
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <FileText className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  Nenhum Documento Assinado
                </h3>
                <p className="text-gray-600 text-sm">
                  Você ainda não possui folhas de ponto assinadas.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Lista de Documentos */
          <div className="space-y-3">
            {documentos.map((doc) => (
              <Card key={doc.id} className="border-l-4 border-l-green-500">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between mb-2">
                    <Badge className="bg-green-600 text-white">
                      ✓ Assinado
                    </Badge>
                    <span className="text-xs text-gray-600">
                      {new Date(doc.data_assinatura).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                  <CardTitle className="text-base">
                    Folha de Ponto - {new Date(doc.periodo_inicio).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Informações */}
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Período:</span>
                      <div className="text-gray-600">
                        {new Date(doc.periodo_inicio).toLocaleDateString('pt-BR')} a{' '}
                        {new Date(doc.periodo_fim).toLocaleDateString('pt-BR')}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Total Trabalhadas:</span>
                      <div className="text-gray-600 text-lg font-bold">{doc.total_horas_trabalhadas}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Empresa:</span>
                      <div className="text-gray-600">{doc.empresa_nome}</div>
                    </div>
                  </div>

                  {/* Botão Download */}
                  <Button
                    onClick={() => baixarPDF(doc)}
                    className="w-full bg-blue-600 hover:bg-blue-700 h-12 flex items-center justify-center space-x-2"
                  >
                    <Download className="h-5 w-5" />
                    <span>Baixar PDF</span>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentosPWA;
